import { adminApiClient } from '@/services/admin/baseService'
import type { ApiResponse } from '@/types'

export type ContentType =
  | 'documentation'
  | 'terms'
  | 'privacy'
  | 'compliance'
  | 'restricted-countries-and-regions'
export type VersionStatus = 'draft' | 'published' | 'archived'
export type SyncLogStatus = 'success' | 'failed' | 'ignored'

export interface DocumentationVersion {
  contentType: string
  versionCode: string
  versionLabel: string
  sourceRef: string
  status: VersionStatus
  isDefault: boolean
  publishedAt: string | null
  updatedAt: string
  pageCount: number
}

export interface DocumentationSyncLog {
  id: string
  contentType: string | null
  event: string
  status: SyncLogStatus
  sourceRef: string | null
  deliveryId: string | null
  repository: string | null
  branch: string | null
  ignored: boolean
  reason: string | null
  summary: {
    matchedChangedFiles: number
    upsertedPages: number
    removedPages: number
    touchedVersions: number
  } | null
  errorMessage: string | null
  startedAt: string
  finishedAt: string | null
  createdAt: string
  action: string
  resourceType: string | null
  resourceId: string | null
  ipAddress: string | null
  userAgent: string | null
  adminUser: {
    id: string
    username: string | null
    email: string | null
    roleName: string | null
  }
}

export interface DocumentationVersionPageSummary {
  slug: string
  title: string
  description: string | null
  navSection: string
  navOrder: number
  effectiveDate: string | null
  lastUpdatedAt: string | null
  updatedAt: string
}

export interface DocumentationVersionPageDetail extends DocumentationVersionPageSummary {
  html: string
  toc: unknown
  sourcePath: string
  sourceSha: string
}

export interface DocumentationVersionPageSection {
  section: string
  pages: DocumentationVersionPageSummary[]
}

export interface DocumentationSettings {
  repository: string
  branch: string
  pathGlob: string
  cache: {
    enabled: boolean
    ttlSeconds: number
    softTtlSeconds: number
    webhookDedupTtlSeconds: number
  }
  auth: {
    webhookSecretConfigured: boolean
    githubReadTokenConfigured: boolean
  }
}

export interface DocumentationConnectivityResult {
  ok: boolean
  checkedAt: string
  repository: string
  branch: string
  pathGlob: string
  tokenConfigured: boolean
  webhookSecretConfigured: boolean
  githubStatus: number | null
  defaultBranch: string | null
  privateRepository: boolean | null
  message: string
}

export interface DocumentationDryRunResult {
  dryRun: boolean
  sourceRef: string
  deliveryId: string | null
  ignored: boolean
  reason?: string
  summary: {
    matchedChangedFiles: number
    upsertedPages: number
    removedPages: number
    touchedVersions: number
  }
  branch: string
  repository: string
  preview: {
    upsertedDocuments: Array<{
      sourcePath: string
      contentType: string
      versionCode: string
      versionLabel: string
      slug: string
      title: string
      navSection: string
      navOrder: number
      effectiveDate: string | null
      lastUpdatedAt: string | null
    }>
    removedDocuments: Array<{
      sourcePath: string
      contentType: string
      versionCode: string
      slug: string
    }>
    warnings: Array<{
      sourcePath: string
      message: string
    }>
  }
}

export interface DocumentationSyncExecutionResult {
  queued?: boolean
  taskId?: string | null
  sourceRef: string
  deliveryId: string | null
  ignored: boolean
  reason?: string
  summary: {
    matchedChangedFiles: number
    upsertedPages: number
    removedPages: number
    touchedVersions: number
  }
  branch: string
  repository: string
  mode?: 'quick' | 'rebuild' | 'force-published'
  contentType?: string | null
  versionCode?: string | null
}

export interface DocumentationPublishExecutionResult {
  queued?: boolean
  taskId?: string | null
  mode?: 'publish'
  contentType: string
  versionCode: string
  setDefault: boolean
  version?: DocumentationVersion
}

export interface DocumentationSyncStatusResult {
  latest: DocumentationSyncLog | null
}

export interface DocumentationDeleteVersionResult {
  contentType: string
  versionCode: string
  deleted: boolean
  deletedPages: number
  previousStatus: VersionStatus
  wasDefault: boolean
  defaultAfterDelete: string | null
}

export interface DocumentationVersionActionExecutionResult {
  queued?: boolean
  taskId?: string | null
  mode?: 'set-default' | 'archive' | 'delete'
  contentType: string
  versionCode: string
  replacementVersionCode?: string | null
}

type ListVersionsParams = {
  contentType: ContentType
  status?: VersionStatus | ''
}

type CreateVersionPayload = {
  contentType: ContentType
  versionCode: string
  versionLabel?: string
  sourceRef?: string
}

type PublishVersionPayload = {
  contentType: ContentType
  setDefault?: boolean
  publishedAt?: string | null
}

type ArchiveVersionPayload = {
  contentType: ContentType
  replacementVersionCode?: string | null
}

type ReplayPayload = {
  payload: Record<string, unknown>
  sourceRef?: string
  deliveryId?: string | null
}

type DryRunPayload = {
  payload: Record<string, unknown>
  sourceRef?: string
  deliveryId?: string | null
}

type QuickSyncPayload = {
  contentType?: ContentType
  deliveryId?: string | null
}

type RebuildSyncPayload = {
  contentType?: ContentType
  deliveryId?: string | null
}

type ForcePublishedSyncPayload = {
  contentType: ContentType
  versionCode: string
  confirmVersionCode: string
  deliveryId?: string | null
}

type DeleteVersionPayload = {
  contentType: ContentType
  confirmVersionCode: string
  replacementVersionCode?: string | null
  reason?: string | null
}

type ListSyncLogsParams = {
  page?: number
  limit?: number
  status?: SyncLogStatus | ''
  sourceRef?: string
  deliveryId?: string
}

type ListVersionPagesParams = {
  contentType: ContentType
  versionCode: string
}

type GetVersionPageParams = {
  contentType: ContentType
  versionCode: string
  slug: string
}

const toNumber = (value: unknown, fallback: number): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const requireString = (value: unknown, field: string): string => {
  if (typeof value !== 'string') {
    throw new Error(`Documentation settings ${field} is required`)
  }
  const normalized = value.trim()
  if (!normalized) {
    throw new Error(`Documentation settings ${field} is required`)
  }
  return normalized
}

const requireNumber = (value: unknown, field: string): number => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    throw new Error(`Documentation settings ${field} is required`)
  }
  return parsed
}

const toBool = (value: unknown, fallback = false): boolean => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true' || normalized === '1') return true
    if (normalized === 'false' || normalized === '0') return false
  }
  return fallback
}

const toStringOrNull = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const normalized = value.trim()
  return normalized.length > 0 ? normalized : null
}

const normalizeVersion = (raw: any): DocumentationVersion => ({
  contentType: raw?.contentType ?? raw?.content_type ?? 'documentation',
  versionCode: raw?.versionCode ?? raw?.version_code ?? '',
  versionLabel: raw?.versionLabel ?? raw?.version_label ?? '',
  sourceRef: raw?.sourceRef ?? raw?.source_ref ?? '',
  status: (raw?.status ?? 'draft') as VersionStatus,
  isDefault: toBool(raw?.isDefault ?? raw?.is_default, false),
  publishedAt: toStringOrNull(raw?.publishedAt ?? raw?.published_at),
  updatedAt: raw?.updatedAt ?? raw?.updated_at ?? '',
  pageCount: toNumber(raw?.pageCount ?? raw?.page_count, 0),
})

const normalizeSyncSummary = (raw: any) => {
  if (!raw || typeof raw !== 'object') {
    return null
  }
  return {
    matchedChangedFiles: toNumber(raw?.matchedChangedFiles ?? raw?.matched_changed_files, 0),
    upsertedPages: toNumber(raw?.upsertedPages ?? raw?.upserted_pages, 0),
    removedPages: toNumber(raw?.removedPages ?? raw?.removed_pages, 0),
    touchedVersions: toNumber(raw?.touchedVersions ?? raw?.touched_versions, 0),
  }
}

const normalizeSyncLog = (raw: any): DocumentationSyncLog => ({
  id: raw?.id ?? '',
  contentType: toStringOrNull(raw?.contentType ?? raw?.content_type),
  event: raw?.event ?? raw?.action ?? 'documentation.sync.replay',
  status: (raw?.status ?? 'success') as SyncLogStatus,
  sourceRef: toStringOrNull(raw?.sourceRef ?? raw?.source_ref),
  deliveryId: toStringOrNull(raw?.deliveryId ?? raw?.delivery_id),
  repository: toStringOrNull(raw?.repository),
  branch: toStringOrNull(raw?.branch),
  ignored: toBool(raw?.ignored, false),
  reason: toStringOrNull(raw?.reason),
  summary: normalizeSyncSummary(raw?.summary),
  errorMessage: toStringOrNull(raw?.errorMessage ?? raw?.error_message),
  startedAt: raw?.startedAt ?? raw?.started_at ?? raw?.createdAt ?? raw?.created_at ?? '',
  finishedAt: toStringOrNull(raw?.finishedAt ?? raw?.finished_at ?? raw?.createdAt ?? raw?.created_at),
  createdAt: raw?.createdAt ?? raw?.created_at ?? '',
  action: raw?.action ?? '',
  resourceType: toStringOrNull(raw?.resourceType ?? raw?.resource_type),
  resourceId: toStringOrNull(raw?.resourceId ?? raw?.resource_id),
  ipAddress: toStringOrNull(raw?.ipAddress ?? raw?.ip_address),
  userAgent: toStringOrNull(raw?.userAgent ?? raw?.user_agent),
  adminUser: {
    id: raw?.adminUser?.id ?? raw?.admin_user?.id ?? '',
    username: toStringOrNull(raw?.adminUser?.username ?? raw?.admin_user?.username),
    email: toStringOrNull(raw?.adminUser?.email ?? raw?.admin_user?.email),
    roleName: toStringOrNull(raw?.adminUser?.roleName ?? raw?.admin_user?.role_name),
  },
})

const normalizePageSummary = (raw: any): DocumentationVersionPageSummary => ({
  slug: raw?.slug ?? '',
  title: raw?.title ?? '',
  description: toStringOrNull(raw?.description),
  navSection: raw?.navSection ?? raw?.nav_section ?? '',
  navOrder: toNumber(raw?.navOrder ?? raw?.nav_order, 0),
  effectiveDate: toStringOrNull(raw?.effectiveDate ?? raw?.effective_date),
  lastUpdatedAt: toStringOrNull(raw?.lastUpdatedAt ?? raw?.last_updated_at),
  updatedAt: raw?.updatedAt ?? raw?.updated_at ?? '',
})

const normalizePageDetail = (raw: any): DocumentationVersionPageDetail => ({
  ...normalizePageSummary(raw),
  html: raw?.html ?? raw?.html_sanitized ?? '',
  toc: raw?.toc ?? null,
  sourcePath: raw?.sourcePath ?? raw?.source_path ?? '',
  sourceSha: raw?.sourceSha ?? raw?.source_sha ?? '',
})

const buildQuery = (params: Record<string, string | number | undefined | null>) => {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return
    }
    search.set(key, String(value))
  })
  const query = search.toString()
  return query.length > 0 ? `?${query}` : ''
}

export const documentationService = {
  async listVersions(params: ListVersionsParams): Promise<{
    contentType: string
    versions: DocumentationVersion[]
  }> {
    const query = buildQuery({
      type: params.contentType,
      status: params.status || undefined,
    })
    const response = await adminApiClient.get<ApiResponse<any>>(`/admin/documentation/versions${query}`)
    const payload = response.data?.data
    const rows = Array.isArray(payload?.versions) ? payload.versions : []
    return {
      contentType: payload?.contentType ?? payload?.content_type ?? params.contentType,
      versions: rows.map((row: any) => normalizeVersion(row)),
    }
  },

  async createVersion(payload: CreateVersionPayload): Promise<DocumentationVersion> {
    const response = await adminApiClient.post<ApiResponse<any>>('/admin/documentation/versions', payload)
    return normalizeVersion(response.data?.data)
  },

  async publishVersion(
    versionCode: string,
    payload: PublishVersionPayload
  ): Promise<DocumentationPublishExecutionResult> {
    const response = await adminApiClient.post<ApiResponse<any>>(
      `/admin/documentation/versions/${encodeURIComponent(versionCode)}/publish`,
      payload
    )
    const data = response.data?.data ?? {}
    const normalizedVersion = normalizeVersion(data)
    const hasVersionShape = Boolean(
      normalizedVersion.versionCode
      && normalizedVersion.contentType
      && normalizedVersion.updatedAt
    )
    return {
      queued: toBool(data?.queued, false),
      taskId: toStringOrNull(data?.taskId ?? data?.task_id),
      mode: data?.mode === 'publish' ? 'publish' : undefined,
      contentType: data?.contentType ?? payload.contentType,
      versionCode: data?.versionCode ?? versionCode,
      setDefault: toBool(data?.setDefault ?? payload.setDefault, false),
      version: hasVersionShape ? normalizedVersion : undefined,
    }
  },

  async setDefaultVersion(
    versionCode: string,
    contentType: ContentType
  ): Promise<DocumentationVersion | DocumentationVersionActionExecutionResult> {
    const response = await adminApiClient.post<ApiResponse<any>>(
      `/admin/documentation/versions/${encodeURIComponent(versionCode)}/set-default`,
      { contentType }
    )
    const data = response.data?.data ?? {}
    if (toBool(data?.queued, false)) {
      return {
        queued: true,
        taskId: toStringOrNull(data?.taskId ?? data?.task_id),
        mode: data?.mode === 'set-default' ? 'set-default' : undefined,
        contentType: data?.contentType ?? contentType,
        versionCode: data?.versionCode ?? versionCode,
      }
    }
    return normalizeVersion(data)
  },

  async archiveVersion(
    versionCode: string,
    payload: ArchiveVersionPayload
  ): Promise<DocumentationVersion | DocumentationVersionActionExecutionResult> {
    const response = await adminApiClient.post<ApiResponse<any>>(
      `/admin/documentation/versions/${encodeURIComponent(versionCode)}/archive`,
      payload
    )
    const data = response.data?.data ?? {}
    if (toBool(data?.queued, false)) {
      return {
        queued: true,
        taskId: toStringOrNull(data?.taskId ?? data?.task_id),
        mode: data?.mode === 'archive' ? 'archive' : undefined,
        contentType: data?.contentType ?? payload.contentType,
        versionCode: data?.versionCode ?? versionCode,
        replacementVersionCode: toStringOrNull(
          data?.replacementVersionCode ?? data?.replacement_version_code
        ),
      }
    }
    return normalizeVersion(data)
  },

  async deleteVersion(
    versionCode: string,
    payload: DeleteVersionPayload
  ): Promise<DocumentationDeleteVersionResult | DocumentationVersionActionExecutionResult> {
    const response = await adminApiClient.delete<ApiResponse<any>>(
      `/admin/documentation/versions/${encodeURIComponent(versionCode)}`,
      {
        data: payload,
      }
    )
    const data = response.data?.data ?? {}
    if (toBool(data?.queued, false)) {
      return {
        queued: true,
        taskId: toStringOrNull(data?.taskId ?? data?.task_id),
        mode: data?.mode === 'delete' ? 'delete' : undefined,
        contentType: data?.contentType ?? data?.content_type ?? payload.contentType,
        versionCode: data?.versionCode ?? data?.version_code ?? versionCode,
      }
    }
    return {
      contentType: data?.contentType ?? data?.content_type ?? payload.contentType,
      versionCode: data?.versionCode ?? data?.version_code ?? versionCode,
      deleted: toBool(data?.deleted, false),
      deletedPages: toNumber(data?.deletedPages ?? data?.deleted_pages, 0),
      previousStatus: (data?.previousStatus ?? data?.previous_status ?? 'draft') as VersionStatus,
      wasDefault: toBool(data?.wasDefault ?? data?.was_default, false),
      defaultAfterDelete: toStringOrNull(data?.defaultAfterDelete ?? data?.default_after_delete),
    }
  },

  async replaySync(payload: ReplayPayload): Promise<any> {
    const response = await adminApiClient.post<ApiResponse<any>>(
      '/admin/documentation/sync/replay',
      payload
    )
    return response.data?.data
  },

  async quickSync(payload: QuickSyncPayload = {}): Promise<DocumentationSyncExecutionResult> {
    const response = await adminApiClient.post<ApiResponse<any>>(
      '/admin/documentation/sync/quick',
      payload,
      { timeout: 180000 }
    )
    const data = response.data?.data ?? {}
    const summary = data?.summary ?? {}
    return {
      queued: toBool(data?.queued, false),
      taskId: toStringOrNull(data?.taskId ?? data?.task_id),
      sourceRef: data?.sourceRef ?? '',
      deliveryId: toStringOrNull(data?.deliveryId),
      ignored: toBool(data?.ignored, false),
      reason: toStringOrNull(data?.reason) ?? undefined,
      summary: {
        matchedChangedFiles: toNumber(summary?.matchedChangedFiles, 0),
        upsertedPages: toNumber(summary?.upsertedPages, 0),
        removedPages: toNumber(summary?.removedPages, 0),
        touchedVersions: toNumber(summary?.touchedVersions, 0),
      },
      branch: data?.branch ?? '',
      repository: data?.repository ?? '',
      mode: data?.mode === 'rebuild' ? 'rebuild' : 'quick',
    }
  },

  async rebuildSync(payload: RebuildSyncPayload = {}): Promise<DocumentationSyncExecutionResult> {
    const response = await adminApiClient.post<ApiResponse<any>>(
      '/admin/documentation/sync/rebuild',
      payload,
      { timeout: 300000 }
    )
    const data = response.data?.data ?? {}
    const summary = data?.summary ?? {}
    return {
      queued: toBool(data?.queued, false),
      taskId: toStringOrNull(data?.taskId ?? data?.task_id),
      sourceRef: data?.sourceRef ?? '',
      deliveryId: toStringOrNull(data?.deliveryId),
      ignored: toBool(data?.ignored, false),
      reason: toStringOrNull(data?.reason) ?? undefined,
      summary: {
        matchedChangedFiles: toNumber(summary?.matchedChangedFiles, 0),
        upsertedPages: toNumber(summary?.upsertedPages, 0),
        removedPages: toNumber(summary?.removedPages, 0),
        touchedVersions: toNumber(summary?.touchedVersions, 0),
      },
      branch: data?.branch ?? '',
      repository: data?.repository ?? '',
      mode: data?.mode === 'quick' ? 'quick' : 'rebuild',
      contentType: toStringOrNull(data?.contentType ?? data?.content_type),
      versionCode: toStringOrNull(data?.versionCode ?? data?.version_code),
    }
  },

  async forcePublishedSync(
    payload: ForcePublishedSyncPayload
  ): Promise<DocumentationSyncExecutionResult> {
    const response = await adminApiClient.post<ApiResponse<any>>(
      '/admin/documentation/sync/force-published',
      payload,
      { timeout: 300000 }
    )
    const data = response.data?.data ?? {}
    const summary = data?.summary ?? {}
    return {
      queued: toBool(data?.queued, false),
      taskId: toStringOrNull(data?.taskId ?? data?.task_id),
      sourceRef: data?.sourceRef ?? '',
      deliveryId: toStringOrNull(data?.deliveryId),
      ignored: toBool(data?.ignored, false),
      reason: toStringOrNull(data?.reason) ?? undefined,
      summary: {
        matchedChangedFiles: toNumber(summary?.matchedChangedFiles, 0),
        upsertedPages: toNumber(summary?.upsertedPages, 0),
        removedPages: toNumber(summary?.removedPages, 0),
        touchedVersions: toNumber(summary?.touchedVersions, 0),
      },
      branch: data?.branch ?? '',
      repository: data?.repository ?? '',
      mode: 'force-published',
      contentType: toStringOrNull(data?.contentType ?? data?.content_type) ?? payload.contentType,
      versionCode: toStringOrNull(data?.versionCode ?? data?.version_code) ?? payload.versionCode,
    }
  },

  async getSyncStatus(): Promise<DocumentationSyncStatusResult> {
    const response = await adminApiClient.get<ApiResponse<any>>('/admin/documentation/sync/status')
    const data = response.data?.data ?? {}
    return {
      latest: data?.latest ? normalizeSyncLog(data.latest) : null,
    }
  },

  async listVersionPages(params: ListVersionPagesParams): Promise<{
    contentType: string
    version: DocumentationVersion
    sections: DocumentationVersionPageSection[]
    pages: DocumentationVersionPageSummary[]
  }> {
    const query = buildQuery({ type: params.contentType })
    const response = await adminApiClient.get<ApiResponse<any>>(
      `/admin/documentation/versions/${encodeURIComponent(params.versionCode)}/pages${query}`
    )
    const payload = response.data?.data ?? {}
    const sections = Array.isArray(payload?.sections) ? payload.sections : []
    const pages = Array.isArray(payload?.pages) ? payload.pages : []
    return {
      contentType: payload?.contentType ?? payload?.content_type ?? params.contentType,
      version: normalizeVersion(payload?.version ?? {}),
      sections: sections.map((section: any) => ({
        section: section?.section ?? '',
        pages: Array.isArray(section?.pages) ? section.pages.map((item: any) => normalizePageSummary(item)) : [],
      })),
      pages: pages.map((row: any) => normalizePageSummary(row)),
    }
  },

  async getVersionPage(params: GetVersionPageParams): Promise<{
    contentType: string
    version: DocumentationVersion
    page: DocumentationVersionPageDetail
  }> {
    const query = buildQuery({ type: params.contentType })
    const response = await adminApiClient.get<ApiResponse<any>>(
      `/admin/documentation/versions/${encodeURIComponent(params.versionCode)}/pages/${encodeURIComponent(params.slug)}${query}`
    )
    const payload = response.data?.data ?? {}
    return {
      contentType: payload?.contentType ?? payload?.content_type ?? params.contentType,
      version: normalizeVersion(payload?.version ?? {}),
      page: normalizePageDetail(payload?.page ?? {}),
    }
  },

  async getSettings(): Promise<DocumentationSettings> {
    const response = await adminApiClient.get<ApiResponse<any>>('/admin/documentation/settings')
    const payload = response.data?.data ?? {}
    return {
      repository: requireString(payload?.repository, 'repository'),
      branch: requireString(payload?.branch, 'branch'),
      pathGlob: requireString(payload?.pathGlob ?? payload?.path_glob, 'pathGlob'),
      cache: {
        enabled: toBool(payload?.cache?.enabled, false),
        ttlSeconds: requireNumber(
          payload?.cache?.ttlSeconds ?? payload?.cache?.ttl_seconds,
          'cache.ttlSeconds'
        ),
        softTtlSeconds: requireNumber(
          payload?.cache?.softTtlSeconds ?? payload?.cache?.soft_ttl_seconds,
          'cache.softTtlSeconds'
        ),
        webhookDedupTtlSeconds: requireNumber(
          payload?.cache?.webhookDedupTtlSeconds ?? payload?.cache?.webhook_dedup_ttl_seconds,
          'cache.webhookDedupTtlSeconds'
        ),
      },
      auth: {
        webhookSecretConfigured: toBool(payload?.auth?.webhookSecretConfigured, false),
        githubReadTokenConfigured: toBool(payload?.auth?.githubReadTokenConfigured, false),
      },
    }
  },

  async testConnectivity(): Promise<DocumentationConnectivityResult> {
    const response = await adminApiClient.post<ApiResponse<any>>(
      '/admin/documentation/settings/connectivity-test',
      {}
    )
    const payload = response.data?.data ?? {}
    return {
      ok: toBool(payload?.ok, false),
      checkedAt: payload?.checkedAt ?? payload?.checked_at ?? '',
      repository: requireString(payload?.repository, 'repository'),
      branch: requireString(payload?.branch, 'branch'),
      pathGlob: requireString(payload?.pathGlob ?? payload?.path_glob, 'pathGlob'),
      tokenConfigured: toBool(payload?.tokenConfigured, false),
      webhookSecretConfigured: toBool(payload?.webhookSecretConfigured, false),
      githubStatus: payload?.githubStatus !== undefined ? toNumber(payload?.githubStatus, 0) : null,
      defaultBranch: toStringOrNull(payload?.defaultBranch ?? payload?.default_branch),
      privateRepository: typeof payload?.privateRepository === 'boolean' ? payload.privateRepository : null,
      message: payload?.message ?? '',
    }
  },

  async dryRunSync(payload: DryRunPayload): Promise<DocumentationDryRunResult> {
    const response = await adminApiClient.post<ApiResponse<any>>(
      '/admin/documentation/sync/dry-run',
      payload
    )
    const data = response.data?.data ?? {}
    const summary = data?.summary ?? {}
    const preview = data?.preview ?? {}
    return {
      dryRun: toBool(data?.dryRun, true),
      sourceRef: data?.sourceRef ?? '',
      deliveryId: toStringOrNull(data?.deliveryId),
      ignored: toBool(data?.ignored, false),
      reason: toStringOrNull(data?.reason) ?? undefined,
      summary: {
        matchedChangedFiles: toNumber(summary?.matchedChangedFiles, 0),
        upsertedPages: toNumber(summary?.upsertedPages, 0),
        removedPages: toNumber(summary?.removedPages, 0),
        touchedVersions: toNumber(summary?.touchedVersions, 0),
      },
      branch: data?.branch ?? '',
      repository: data?.repository ?? '',
      preview: {
        upsertedDocuments: Array.isArray(preview?.upsertedDocuments)
          ? preview.upsertedDocuments.map((item: any) => ({
              sourcePath: item?.sourcePath ?? '',
              contentType: item?.contentType ?? '',
              versionCode: item?.versionCode ?? '',
              versionLabel: item?.versionLabel ?? '',
              slug: item?.slug ?? '',
              title: item?.title ?? '',
              navSection: item?.navSection ?? '',
              navOrder: toNumber(item?.navOrder, 0),
              effectiveDate: toStringOrNull(item?.effectiveDate),
              lastUpdatedAt: toStringOrNull(item?.lastUpdatedAt),
            }))
          : [],
        removedDocuments: Array.isArray(preview?.removedDocuments)
          ? preview.removedDocuments.map((item: any) => ({
              sourcePath: item?.sourcePath ?? '',
              contentType: item?.contentType ?? '',
              versionCode: item?.versionCode ?? '',
              slug: item?.slug ?? '',
            }))
          : [],
        warnings: Array.isArray(preview?.warnings)
          ? preview.warnings.map((item: any) => ({
              sourcePath: item?.sourcePath ?? '',
              message: item?.message ?? '',
            }))
          : [],
      },
    }
  },

  async listSyncLogs(params: ListSyncLogsParams): Promise<{
    logs: DocumentationSyncLog[]
    pagination: {
      page: number
      limit: number
      total: number
      pages: number
    }
  }> {
    const query = buildQuery({
      page: params.page ?? 1,
      limit: params.limit ?? 20,
      status: params.status || undefined,
      sourceRef: params.sourceRef || undefined,
      deliveryId: params.deliveryId || undefined,
    })
    const response = await adminApiClient.get<ApiResponse<any>>(
      `/admin/documentation/sync/logs${query}`
    )
    const payload = response.data?.data ?? {}
    const logs = Array.isArray(payload?.logs) ? payload.logs : []
    const pagination = payload?.pagination ?? {}

    return {
      logs: logs.map((row: any) => normalizeSyncLog(row)),
      pagination: {
        page: toNumber(pagination?.page, params.page ?? 1),
        limit: toNumber(pagination?.limit, params.limit ?? 20),
        total: toNumber(pagination?.total, logs.length),
        pages: toNumber(pagination?.pages, 1),
      },
    }
  },
}
